import React, { useRef } from "react"

export const DEFAULT_PANEL_WIDTH = 420
// Below this the extracted-field rows (label, value, confidence, copy) start wrapping.
export const MIN_PANEL_WIDTH = 340
export const MAX_PANEL_WIDTH = 900

const KEYBOARD_STEP = 24

/**
 * The grab handle between the document and the side panel.
 *
 * Uses pointer capture rather than listeners on the document. The viewer next to it
 * renders PDFs in an iframe, and an iframe takes pointer events into its own document --
 * so a mouseup released over the PDF never reached the parent and the drag stayed live
 * until the next click landed elsewhere. Capturing the pointer routes every move and the
 * release back to this handle whatever is underneath.
 *
 * Width is tracked as a delta from where the drag started, so it needs no measurement of
 * the layout and behaves the same wherever the modal sits. Double click, or Home, puts
 * it back to the default.
 */
const PanelResizer = ({
    width,
    onWidthChange,
    min = MIN_PANEL_WIDTH,
    max = MAX_PANEL_WIDTH,
    defaultWidth = DEFAULT_PANEL_WIDTH,
}) => {
    const dragRef = useRef(null)

    const clamp = (value) => Math.min(max, Math.max(min, value))

    const startDragging = (event) => {
        event.preventDefault()
        dragRef.current = { startX: event.clientX, startWidth: width }

        if (event.currentTarget.setPointerCapture) {
            event.currentTarget.setPointerCapture(event.pointerId)
        }
        // Keeps the cursor consistent and stops the document selecting text mid-drag.
        document.body.classList.add("idm-resizing")
    }

    const keepDragging = (event) => {
        if (!dragRef.current) return
        // The panel is on the right, so dragging left has to widen it.
        const travelled = event.clientX - dragRef.current.startX
        onWidthChange(clamp(dragRef.current.startWidth - travelled))
    }

    const stopDragging = (event) => {
        if (!dragRef.current) return
        dragRef.current = null
        document.body.classList.remove("idm-resizing")

        const handle = event.currentTarget
        if (handle && handle.releasePointerCapture && handle.hasPointerCapture && handle.hasPointerCapture(event.pointerId)) {
            handle.releasePointerCapture(event.pointerId)
        }
    }

    // Double click to reset, the way a splitter usually behaves.
    const resetWidth = () => onWidthChange(clamp(defaultWidth))

    const onKeyDown = (event) => {
        if (event.key === "ArrowLeft") {
            event.preventDefault()
            onWidthChange(clamp(width + KEYBOARD_STEP))
        }
        if (event.key === "ArrowRight") {
            event.preventDefault()
            onWidthChange(clamp(width - KEYBOARD_STEP))
        }
        // Same reset from the keyboard, for anyone who cannot double click.
        if (event.key === "Home") {
            event.preventDefault()
            resetWidth()
        }
    }

    return (
        <div
            className="idm-resizer"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize panel"
            aria-valuenow={width}
            aria-valuemin={min}
            aria-valuemax={max}
            tabIndex={0}
            title="Drag to resize &middot; double-click to reset"
            onDoubleClick={resetWidth}
            onPointerDown={startDragging}
            onPointerMove={keepDragging}
            onPointerUp={stopDragging}
            onPointerCancel={stopDragging}
            onLostPointerCapture={stopDragging}
            onKeyDown={onKeyDown}
        />
    )
}

export default PanelResizer
